import { Link } from "@tanstack/react-router"
import { ArrowRight, Check, ChevronDown, CircleAlert } from "lucide-react"
import type { ReactNode } from "react"
import { PublicNavbar } from "@/components/public-navbar"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"

type Step = {
  title: string
  copy: string
}

type Question = {
  question: string
  answer: string
}

type RelatedPage = {
  eyebrow: string
  title: string
  copy: string
  href: string
}

export type UseCasePageProps = {
  badge: string
  eyebrow: string
  title: string
  lead: string
  sourceLine: string
  proofTitle: string
  proofCopy: string
  proofPoints: string[]
  steps: Step[]
  stepsTitle: string
  stepsCopy: string
  limitsTitle: string
  limits: string[]
  questions: Question[]
  related: RelatedPage[]
  visual?: ReactNode
}

export function UseCasePage({
  badge,
  eyebrow,
  title,
  lead,
  sourceLine,
  proofTitle,
  proofCopy,
  proofPoints,
  steps,
  stepsTitle,
  stepsCopy,
  limitsTitle,
  limits,
  questions,
  related,
  visual,
}: UseCasePageProps) {
  return (
    <div className="landing forge-landing use-case-page">
      <a className="skip-link" href="#use-case-content">
        Skip to content
      </a>
      <PublicNavbar />
      <main id="use-case-content" className="forge-main">
        <section className="use-case-hero page-width">
          <div className="use-case-hero-copy">
            <Badge variant="outline" className="forge-badge">
              {badge}
            </Badge>
            <p className="use-case-eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
            <p className="use-case-lead">{lead}</p>
            <div className="use-case-actions">
              <Link
                to="/app"
                className={buttonVariants({
                  size: "lg",
                  className: "forge-primary-link",
                })}
              >
                Start a project
                <ArrowRight data-icon="inline-end" aria-hidden="true" />
              </Link>
              <Link
                to="/pricing"
                className={buttonVariants({ variant: "outline", size: "lg" })}
              >
                See pricing
              </Link>
            </div>
            <p className="use-case-source-line">
              <Check aria-hidden="true" /> {sourceLine}
            </p>
          </div>
        </section>

        <section
          className="use-case-proof page-width"
          aria-labelledby="proof-title"
        >
          <div className="use-case-proof-copy">
            <p>What intoshort does</p>
            <h2 id="proof-title">{proofTitle}</h2>
            <p>{proofCopy}</p>
          </div>
          <ul>
            {proofPoints.map((point) => (
              <li key={point}>
                <Check aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>

        {visual}

        <section
          className="use-case-process page-width"
          aria-labelledby="process-title"
        >
          <header>
            <p>From source to export</p>
            <h2 id="process-title">{stepsTitle}</h2>
            <span>{stepsCopy}</span>
          </header>
          <ol>
            {steps.map((step, index) => (
              <li key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="use-case-limits page-width"
          aria-labelledby="limits-title"
        >
          <div>
            <CircleAlert aria-hidden="true" />
            <p>Before you process</p>
            <h2 id="limits-title">{limitsTitle}</h2>
          </div>
          <ul>
            {limits.map((limit) => (
              <li key={limit}>{limit}</li>
            ))}
          </ul>
        </section>

        <section
          className="use-case-faq page-width"
          aria-labelledby="use-case-faq-title"
        >
          <header>
            <p>Questions</p>
            <h2 id="use-case-faq-title">Clear answers before you start.</h2>
          </header>
          <div>
            {questions.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <ChevronDown aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section
          className="use-case-related page-width"
          aria-labelledby="related-title"
        >
          <header>
            <p>More ways to start</p>
            <h2 id="related-title">Match the workflow to your footage.</h2>
          </header>
          <div>
            {related.map((item) => (
              <a href={item.href} key={item.href}>
                <span>{item.eyebrow}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
                <ArrowRight aria-hidden="true" />
              </a>
            ))}
          </div>
        </section>

        <section
          className="forge-final-cta page-width"
          aria-labelledby="use-case-cta-title"
        >
          <div>
            <p>Your footage, your decision.</p>
            <h2 id="use-case-cta-title">Start with a draft you can change.</h2>
            <span>
              intoshort prepares the first pass. Review the moment, the cut and
              the captions before you export.
            </span>
          </div>
          <Link
            to="/app"
            className={buttonVariants({
              size: "lg",
              className: "forge-primary-link",
            })}
          >
            Open intoshort
            <ArrowRight data-icon="inline-end" aria-hidden="true" />
          </Link>
        </section>
      </main>
    </div>
  )
}
